import React from 'react';
import {
    FaUser,
    FaIdCard,
    FaClock,
    FaMapMarkerAlt,
    FaRegStickyNote,
    FaBuilding,
    FaMapPin,
    FaPhone,
} from 'react-icons/fa';
import EstimatedDelivery from './EstimatedDelivery';

const extraHoursNote = (extraHours) => {
    const hours = Number(extraHours);
    const value = Number.isFinite(hours) ? hours : 0;
    if (value === 1) return '+1 hora adicional';
    return `+${value} horas adicionales`;
};

const usablePhone = (value) => {
    const phone = (value || '').trim();
    if (!phone || phone === 'Teléfono no disponible' || phone === '0999999999' || phone === '9999999999') {
        return '';
    }
    return phone;
};

const ShippingAddress = ({
    address,
    orderDate,
    isPaid,
    showEstimatedDelivery = true,
    pickupCompanyInfo = null,
}) => {
    if (!address) return null;

    const isPickup = address.label === 'pickup';
    const isAdditionalPickup = isPickup && Boolean(address.pickupLocationId);
    const additionalLocations = Array.isArray(pickupCompanyInfo?.additionalPickupLocations)
        ? pickupCompanyInfo.additionalPickupLocations
        : [];
    const matchedLocation = isAdditionalPickup
        ? additionalLocations.find(
            (location) => location && String(location._id) === String(address.pickupLocationId)
        )
        : null;

    const companyName =
        pickupCompanyInfo?.tradeName ||
        pickupCompanyInfo?.legalName ||
        pickupCompanyInfo?.name ||
        (!isAdditionalPickup ? address.name : '') ||
        'Comercial Santy';
    const pickupTitle = isAdditionalPickup
        ? matchedLocation?.name || address.reference || address.name || 'Punto de retiro'
        : 'Recoger en el local';
    const pickupAddress = isAdditionalPickup
        ? [
            address.address1 || matchedLocation?.address,
            address.city || matchedLocation?.city,
            address.state || matchedLocation?.state || pickupCompanyInfo?.state,
        ].filter(Boolean).join(', ')
        : [
            pickupCompanyInfo?.location?.address || pickupCompanyInfo?.address || address.address1,
            pickupCompanyInfo?.city || address.city,
            pickupCompanyInfo?.state || pickupCompanyInfo?.location?.state || address.state,
        ].filter(Boolean).join(', ');
    const pickupPhone = usablePhone(
        isAdditionalPickup
            ? address.phoneNumber || matchedLocation?.phoneNumber || pickupCompanyInfo?.phoneNumber
            : pickupCompanyInfo?.phoneNumber || pickupCompanyInfo?.phone || address.phoneNumber
    );
    const pickupGoogleMapsUrl = isAdditionalPickup
        ? address.googleMapsUrl || matchedLocation?.googleMapsUrl || ''
        : pickupCompanyInfo?.location?.googleMapsUrl ||
          pickupCompanyInfo?.googleMapsUrl ||
          address.googleMapsUrl ||
          '';
    const openingHours = !isAdditionalPickup && Array.isArray(pickupCompanyInfo?.openingHours)
        ? pickupCompanyInfo.openingHours.filter(Boolean)
        : [];
    const availabilityNote = isAdditionalPickup
        ? extraHoursNote(address.extraHours ?? matchedLocation?.extraHours)
        : 'Disponible de inmediato';

    return (
        <>
            {!isPickup && <h2 className="fs-4">Dirección de Envío</h2>}
            <div className={isPickup ? '' : 'd-flex flex-column gap-2 text-muted small'}>
                {isPickup ? (
                    <div className="pickup-summary">
                        <span className="pickup-summary-kicker">
                            {isAdditionalPickup ? 'Punto de retiro' : 'Retiro en tienda'}
                        </span>
                        <h2>{pickupTitle}</h2>
                        {isAdditionalPickup && companyName && pickupTitle !== companyName && (
                            <div className="pickup-summary-company">{companyName}</div>
                        )}
                        {!isAdditionalPickup && (
                            <div className="pickup-summary-company">{companyName}</div>
                        )}

                        {pickupAddress && (
                            <div className="d-flex align-items-start gap-2">
                                <FaMapMarkerAlt className="pickup-summary-icon mt-1" />
                                <span className="text-break">
                                    <strong>Dirección:</strong> {pickupAddress}
                                </span>
                            </div>
                        )}

                        {pickupPhone && (
                            <div className="d-flex align-items-start gap-2">
                                <FaPhone className="pickup-summary-icon mt-1" />
                                <span className="text-break">
                                    <strong>Teléfono:</strong>{' '}
                                    <a href={`tel:${pickupPhone}`} className="text-reset text-decoration-none">
                                        {pickupPhone}
                                    </a>
                                </span>
                            </div>
                        )}

                        <div className="pickup-summary-note">{availabilityNote}</div>

                        {pickupGoogleMapsUrl && (
                            <a
                                href={pickupGoogleMapsUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="pickup-summary-map"
                            >
                                <FaMapMarkerAlt aria-hidden="true" />
                                Cómo llegar
                            </a>
                        )}

                        {openingHours.length > 0 && (
                            <div className="d-flex align-items-start gap-2">
                                <FaClock className="pickup-summary-icon mt-1" />
                                <div>
                                    <strong>Horario:</strong>
                                    <ul className="mb-0 ps-3">
                                        {openingHours.map((hour, index) => (
                                            <li key={index}>{hour}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <>
                <div className="d-flex align-items-center gap-2">
                    <FaUser className="text-primary" />
                    <span>
                        <strong>Nombre:</strong> {address.name}
                    </span>
                </div>

                <div className="d-flex align-items-center gap-2">
                    <FaIdCard className="text-primary" />
                    <span>
                        <strong>Doc. Ced/RUC:</strong> {address.taxDocId || address.docId} ({address.type === 'J' ? 'Jurídica' : address.type === 'NF' ? 'Extranjero' : 'Natural'})
                    </span>
                </div>

                <div className="d-flex align-items-center gap-2">
                    <FaMapMarkerAlt className="text-primary" />
                    <span>
                        <strong>Calle Principal:</strong> {address.address1} {address.number}
                    </span>
                </div>

                {address.address2 && (
                    <div className="d-flex align-items-center gap-2">
                        <FaMapMarkerAlt className="text-primary" />
                        <span>
                            <strong>Calle Secundaria:</strong> {address.address2}
                        </span>
                    </div>
                )}

                {address.reference && (
                    <div className="d-flex align-items-center gap-2">
                        <FaRegStickyNote className="text-primary" />
                        <span>
                            <strong>Referencia:</strong> {address.reference}
                        </span>
                    </div>
                )}

                <div className="d-flex align-items-center gap-2">
                    <FaBuilding className="text-primary" />
                    <span>
                        <strong>Ciudad:</strong> {address.city}
                    </span>
                </div>

                <div className="d-flex align-items-center gap-2">
                    <FaMapPin className="text-primary" />
                    <span>
                        <strong>Provincia:</strong> {address.state}
                    </span>
                </div>

                <div className="d-flex align-items-center gap-2">
                    <FaPhone className="text-primary" />
                    <span>
                        <strong>Teléfono:</strong> {address.phoneNumber}
                    </span>
                </div>
                    </>
                )}

                {showEstimatedDelivery && !isPickup && (
                    <EstimatedDelivery tag={address.label} orderDate={orderDate} isPaid={isPaid} />
                )}
            </div>
        </>
    );
};

export default ShippingAddress;
